import { ChevronRight } from "@tamagui/lucide-icons";
import { useContext } from "react";
import { type GetProps, View, XStack, YStack, useTheme } from "tamagui";

import { getShadow } from "@/theme/helpers";
import { Button } from "./Button";
import { LayoutContext } from "./LayoutProvider";
import Logo from "./Logo";
import { Skeleton } from "./Skeletons";
import { Heading, Paragraph } from "./Typography";

type ChatNoteProps = {
  onPress?: () => void;
  Icon: React.ElementType;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  disclaimer?: string;
};

export default function ChatNote({
  onPress,
  Icon,
  title,
  subtitle,
  disclaimer,
}: ChatNoteProps) {
  const Container = onPress ? Button.Wrapper : XStack;
  const containerProps = onPress
    ? {
        onPress,
        backgroundColor: "$fillCallout",
        ...getShadow(),
        dataTrackingId: "chat-note",
        focusVisibleStyle: {
          outlineWidth: 2,
          outlineOffset: 2,
          outlineColor: "$a11yBlue",
          outlineStyle: "solid",
        },
      }
    : {
        backgroundColor: "$fillCallout",
        outlineWidth: 2,
        outlineColor: "$strokeNeutral",
        outlineStyle: "solid",
      };

  const theme = useTheme();
  return (
    <Container
      gap="$200"
      padding="$300"
      borderRadius="$150"
      {...containerProps}
    >
      <View flexShrink={0} flexGrow={0}>
        <Icon color={theme.iconBase.get()} />
      </View>
      <YStack flex={1}>
        <Heading size="$md" marginBottom="$50">
          {title}
        </Heading>
        {!!subtitle && (
          <Paragraph size="$button" marginBottom="$100">
            {subtitle}
          </Paragraph>
        )}
        {!!disclaimer && (
          <Paragraph fontStyle="italic" size="$sm">
            {disclaimer}
          </Paragraph>
        )}
      </YStack>
      <View flexShrink={0} flexGrow={0} alignSelf="center">
        {!!onPress && <ChevronRight size="$225" />}
      </View>
    </Container>
  );
}

ChatNote.Active = ({
  Icon,
  title,
  subtitle,
  disclaimer,
  button,
  onPress,
  ...rest
}: ChatNoteProps & { button?: React.ReactNode } & GetProps<typeof XStack>) => {
  const theme = useTheme();
  const { lg } = useContext(LayoutContext);
  const Container = onPress ? Button.Wrapper : XStack;
  const containerProps = onPress
    ? {
        onPress,
        dataTrackingId: "chat-note",
        focusVisibleStyle: {
          outlineWidth: 2,
          outlineOffset: 2,
          outlineColor: "$a11yBlue",
          outlineStyle: "solid",
        },
      }
    : {};
  return (
    <Container
      gap="$200"
      padding="$300"
      borderRadius="$150"
      {...getShadow()}
      backgroundColor="$fillCallout"
      {...containerProps}
      {...rest}
    >
      <View flexShrink={0} flexGrow={0}>
        <Icon color={theme.iconBase.get()} />
      </View>
      <YStack flex={1} alignItems="flex-start">
        <Heading size="$md" marginBottom="$50">
          {title}
        </Heading>
        {!!subtitle && (
          <Paragraph size="$button" marginBottom="$100">
            {subtitle}
          </Paragraph>
        )}
        {!lg && button}
        {!!disclaimer && (
          <Paragraph fontStyle="italic" size="$sm">
            {disclaimer}
          </Paragraph>
        )}
      </YStack>
      {lg && button}
    </Container>
  );
};

ChatNote.Skeleton = () => {
  return (
    <ChatNote.Active Icon={Logo.Only} title={<Skeleton.Text lines={2} />} />
  );
};
